import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import cn from 'classnames';
import RsTableRow from '@Pages/RetroSynthesis/components/RsTable/RsTableRow';
import RsEditableItem from '@Pages/RetroSynthesis/components/RsEditableItem';
import RsStructure from '@Pages/RetroSynthesis/components/RsStructure';
import ProjectStatusGridItem from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectStatusGridItem';

import ProjectParametersGridItem from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectParametersGridItem';
import ProjectNoParameters from '@Pages/RetroSynthesis/RetroSynthesisProjects/components/ProjectNoParameters';
import { getProjectRoutesTypeForAA, getProjectDetail } from '@Pages/RetroSynthesis/constants';
import { Checkbox } from '@els/els-react--checkbox';
import { ButtonWithIcon } from '@els/els-react--button';
import { TooltipConstants } from '@Components/Tooltip';
import trim from 'lodash/trim';
import HoverTooltip from '@Components/HoverTooltip';
import { TEXT_INPUT_DEFAULT_ALERT_MESSAGE } from '@Components/Input/const';
import { validateMaxTextLength, validateMinTextLength } from '@Components/Input/HOCs/utils/validator';

import './RsProjectTableItem.scss';

const dateFormat = (value) => (value ? moment(value).format('DD MMM YYYY') : null);

const validateProjectName = (name) => {
  const nameLength = trim(name).length;
  const isValid = validateMinTextLength(nameLength) && validateMaxTextLength(nameLength);

  return {
    isValid,
    validationMessage: <span role={isValid ? 'tooltip' : 'alert'}>{TEXT_INPUT_DEFAULT_ALERT_MESSAGE}</span>,
    preventChange: false,
  };
};

function ProjectTableItem({
  id,
  project,
  onEditName,
  onSimilaritySearch,
  openSynthesisJobRetryDialog,
  onViewResults,
  showParams,
  onToggleParamsDisplay,
  onEdit,
  onDelete,
  selectedIds,
  onSelectionChange,
  onRerun,
  doRetrySynthesisJob,
  className,
}) {
  const {
    name,
    modified,
    targetStructure,
    publishedParameters,
    predictedParameters,
    queuePosition,
    publishedRouteCount,
    customizedRouteCount,
    combinedRouteCount,
    rtRouteCount,
    rtStatus,
    publishedStatus,
    rtErrorReason,
    structureDetails,
    step = 0,
  } = project;
  const [isEditMode, setEditMode] = useState(false);
  const trackingContent = [
    {
      id: `project:${id}`,
      detail: getProjectDetail(project),
      type: getProjectRoutesTypeForAA(project),
    },
  ];
  const onParamsClick = () => {
    if (!showParams) {
      onToggleParamsDisplay();
    }
  };
  const partner = predictedParameters ? predictedParameters.partner : null;
  return (
    <RsTableRow className={className}>
      <td className="table-row__left c-els-table__cell c-els-table__basic-cell">
        <div className="table-row__group-border-left" />
        <Checkbox
          data-e2e={`select-project-${id}`}
          onChange={(e) => {
            const { checked } = e.currentTarget;
            onSelectionChange &&
              e &&
              e.target &&
              id &&
              onSelectionChange({
                targetId: id,
                value: !!checked,
              });
          }}
          id={`project-checkbox-${id}`}
          label={`Select project ${name}`}
          shouldHideLabel
          checked={selectedIds.includes(id)}
          className="project-checkbox"
        />
      </td>
      <td className={cn('c-els-table__cell c-els-table__basic-cell')}>{id}</td>
      <td className={cn('c-els-table__cell c-els-table__basic-cell')}>{dateFormat(modified)}</td>
      <td className="table-row__name c-els-table__cell c-els-table__basic-cell">
        <div className="table-row__name__inner">
          <RsEditableItem
            value={name}
            isEditMode={isEditMode}
            setEditMode={setEditMode}
            onValueEdited={(newValue) => onEditName(id, newValue)}
            validateValueInput={validateProjectName}
            trackingContent={trackingContent}
          />
        </div>
        <div className="table-row__name__btns">
          <ButtonWithIcon
            data-e2e={`delete-project-${id}`}
            className="e2e-rs-project-table-item-button-delete"
            aria-label="Delete project"
            sprite="Trash"
            iconRight
            type="link"
            onClick={() => onDelete(id)}>
            Delete
          </ButtonWithIcon>
        </div>
      </td>
      <td className={cn('table-row__structure c-els-table__cell c-els-table__basic-cell', { collapsed: !showParams })}>
        <RsStructure structure={targetStructure} trackingContent={trackingContent} />
        <ButtonWithIcon
          data-e2e="test-id"
          className="table-row__structure__btns e2e-rs-project-table-item-button-edit"
          sprite="Edit"
          aria-label="Edit Project"
          aria-description="This field allows you to edit the project name. Please note that deleting the project will result in the deletion of the associated project."
          iconRight
          type="link"
          onClick={() => onEdit(project)}>
          Edit
        </ButtonWithIcon>
        {structureDetails && structureDetails.length > 0 && !showParams && (
          <div className="structure-details">
            {structureDetails.map((detail, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="structure-detail">
                *{detail}
              </div>
            ))}
          </div>
        )}
      </td>
      {!showParams ? (
        <td
          onClick={onParamsClick}
          className={cn(
            'table-row__parameters c-els-table__cell c-els-table__basic-cell',
            'table-row__parameters--collapsed',
            'interactive'
          )}>
          <HoverTooltip
            tag="div"
            id={TooltipConstants.ids.RetrosynthesisParams}
            place="bottom"
            theme="simple"
            tooltip={`${showParams ? 'Close' : 'Show'} parameters`}>
            <ProjectNoParameters message="" />
          </HoverTooltip>
        </td>
      ) : (
        <td
          className={cn(
            'table-row__parameters c-els-table__cell c-els-table__basic-cell',
            !showParams && 'table-row__parameters--collapsed'
          )}
          onClick={onParamsClick}>
          {showParams && (publishedParameters || predictedParameters) ? (
            <ProjectParametersGridItem publishedParameters={publishedParameters} predictedParameters={predictedParameters} step={step} />
          ) : (
            showParams && <ProjectNoParameters />
          )}
        </td>
      )}
      <td className="table-row__status c-els-table__cell c-els-table__basic-cell">
        <ProjectStatusGridItem
          projectId={id}
          publishedStatus={publishedStatus}
          predictedStatus={rtStatus}
          predictedParameters={predictedParameters}
          publishedRoutesCount={publishedRouteCount}
          predictedRoutesCount={rtRouteCount}
          customizedRouteCount={customizedRouteCount}
          combinedRouteCount={combinedRouteCount}
          onInteraction={() => onSimilaritySearch(project)}
          openSynthesisJobRetryDialog={(status, type) => openSynthesisJobRetryDialog(project, status, type, doRetrySynthesisJob)}
          onResults={() => onViewResults(project)}
          onEdit={() => onEdit(project)}
          rtErrorReason={rtErrorReason}
          onRerun={(types) => onRerun(id, types)}
          queuePosition={queuePosition}
          step={step}
          partner={partner}
        />
        <div className="table-row__group-border-right" />
      </td>
    </RsTableRow>
  );
}

ProjectTableItem.defaultProps = {
  onEditName: () => null,
  onSimilaritySearch: () => null,
  openSynthesisJobRetryDialog: () => null,
  onViewResults: () => null,
  onEdit: () => null,
  onDelete: () => null,
  onSelectionChange: () => null,
  onRerun: () => null,
  doRetrySynthesisJob: () => null,
  selectedIds: [],
};

ProjectTableItem.propTypes = {
  id: PropTypes.number.isRequired,
  project: PropTypes.object.isRequired,
  onEditName: PropTypes.func,
  onSimilaritySearch: PropTypes.func,
  openSynthesisJobRetryDialog: PropTypes.func,
  onViewResults: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  selectedIds: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onToggleParamsDisplay: PropTypes.func,
  doRetrySynthesisJob: PropTypes.func,
  onRerun: PropTypes.func,
  showParams: PropTypes.bool,
  className: PropTypes.string,
};

export default ProjectTableItem;
